/**
 * SmsGatewaysPage — the "SMS Gateways" section of the notifications admin area.
 *
 * Mounted at /admin/sms. Everything here is organisation scoped by the server:
 * an office sees its own gateway, its own usage and its own replies.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Radio, Inbox, BarChart3, Plus, Pause, Play, Ban, Archive,
  Send, Loader2, Smartphone,
} from 'lucide-react';
import { smsAPI, getErrorMessage } from '@/lib/api';
import SmsGatewayWizard from '@/components/sms/SmsGatewayWizard';

const STATUS_CLR = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  registered: 'bg-slate-50 text-slate-600 border-slate-200',
  paired: 'bg-sky-50 text-sky-700 border-sky-200',
  verifying: 'bg-amber-50 text-amber-700 border-amber-200',
  suspended: 'bg-amber-50 text-amber-800 border-amber-300',
  revoked: 'bg-rose-50 text-rose-700 border-rose-200',
  retired: 'bg-slate-100 text-slate-500 border-slate-200',
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[0.7rem] uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className="text-lg font-semibold text-slate-900">{value}</p>
  </div>
);

const GatewayCard = ({ row, onLifecycle, busy }) => (
  <Card data-testid={`gateway-card-${row.id}`}>
    <CardContent className="pt-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900" data-testid={`gateway-name-${row.id}`}>
            {row.name}
          </h3>
          <p className="text-sm text-slate-500">
            {row.sending_number} · {row.country} · {row.organisation_name}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {row.provider} · {row.account_mode === 'own_account'
              ? 'own SMS8 account' : 'FWM-held account'} · bill paid by{' '}
            {row.billing_owner === 'organisation' ? 'this organisation' : 'FWM'}
            {row.test_mode ? ' · test mode' : ''}
          </p>
        </div>
        <Badge variant="outline" className={`capitalize ${STATUS_CLR[row.status] || ''}`}
               data-testid={`gateway-status-${row.id}`}>
          {row.status}
        </Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t pt-4">
        <Stat label="Sent today" value={row.usage?.sent_today ?? 0} />
        <Stat label="This month" value={row.usage?.sent_this_month ?? 0} />
        <Stat label="Failed" value={row.usage?.failed_this_month ?? 0} />
        <Stat label={`Est. cost (${row.currency || ''})`}
              value={(row.usage?.estimated_cost_this_month ?? 0).toFixed(2)} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {row.status === 'active' && (
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => onLifecycle(row.id, 'suspend')}
                  data-testid={`gateway-suspend-${row.id}`}>
            <Pause className="h-3.5 w-3.5 mr-1" /> Suspend
          </Button>
        )}
        {row.status === 'suspended' && (
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => onLifecycle(row.id, 'reactivate')}
                  data-testid={`gateway-reactivate-${row.id}`}>
            <Play className="h-3.5 w-3.5 mr-1" /> Reactivate
          </Button>
        )}
        {!['revoked', 'retired'].includes(row.status) && (
          <Button size="sm" variant="outline" disabled={busy}
                  className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  onClick={() => onLifecycle(row.id, 'revoke')}
                  data-testid={`gateway-revoke-${row.id}`}>
            <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
          </Button>
        )}
        {row.status !== 'retired' && (
          <Button size="sm" variant="ghost" disabled={busy}
                  onClick={() => onLifecycle(row.id, 'retire')}
                  data-testid={`gateway-retire-${row.id}`}>
            <Archive className="h-3.5 w-3.5 mr-1" /> Retire
          </Button>
        )}
        {row.credential_stored && (
          <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
            <Smartphone className="h-3.5 w-3.5" />
            key ••••{row.api_key_last4} · device {row.device_id || '—'}
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

const TestSend = ({ gateways }) => {
  const qc = useQueryClient();
  const live = gateways.filter((g) => g.status === 'active');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('Test from the FWM messaging platform.');
  const [orgId, setOrgId] = useState(live[0]?.organisation_id || '');
  const send = useMutation({
    mutationFn: () => smsAPI.send({ organisation_id: orgId, to, body, purpose: 'operational' }),
    onSuccess: (r) => {
      toast.success(
        `${r.data.status}${r.data.test_mode ? ' (test mode — nothing left the server)' : ''} · via ${r.data.gateway_name}`);
      qc.invalidateQueries({ queryKey: ['sms-usage'] });
      qc.invalidateQueries({ queryKey: ['sms-gateways'] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Send failed')),
  });
  if (!live.length) {
    return <p className="text-sm text-slate-500" data-testid="test-send-empty">
      Activate a gateway first — the router refuses to borrow somebody else's SIM.
    </p>;
  }
  return (
    <div className="space-y-3 max-w-lg" data-testid="sms-test-send">
      <div>
        <Label>Send as organisation</Label>
        <select className="w-full border rounded-md h-10 px-3 text-sm"
                value={orgId} onChange={(e) => setOrgId(e.target.value)}
                data-testid="test-send-org">
          {live.map((g) => (
            <option key={g.id} value={g.organisation_id}>
              {g.organisation_name} — {g.sending_number}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label>To (E.164)</Label>
        <Input value={to} onChange={(e) => setTo(e.target.value.trim())}
               placeholder="+263771234567" data-testid="test-send-to" />
      </div>
      <div>
        <Label>Message</Label>
        <Input value={body} onChange={(e) => setBody(e.target.value)}
               data-testid="test-send-body" />
      </div>
      <Button onClick={() => send.mutate()} disabled={!to || !body || send.isPending}
              data-testid="test-send-btn">
        {send.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <Send className="h-4 w-4 mr-2" />}
        Send
      </Button>
    </div>
  );
};

export default function SmsGatewaysPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-gateways'],
    queryFn: () => smsAPI.listGateways().then((r) => r.data),
  });
  const { data: usage } = useQuery({
    queryKey: ['sms-usage'],
    queryFn: () => smsAPI.usage().then((r) => r.data),
  });
  const { data: replies } = useQuery({
    queryKey: ['sms-inbox'],
    queryFn: () => smsAPI.inbox().then((r) => r.data),
  });

  const lifecycle = useMutation({
    mutationFn: ({ id, action }) => smsAPI.lifecycle(id, action),
    onSuccess: (r) => {
      toast.success(`Gateway ${r.data.status}`
        + (r.data.cancelled_pending ? ` · ${r.data.cancelled_pending} pending message(s) cancelled` : ''));
      qc.invalidateQueries({ queryKey: ['sms-gateways'] });
      qc.invalidateQueries({ queryKey: ['sms-usage'] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not change the gateway')),
  });

  const rows = data?.items || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sms-gateways-page">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin" className="text-slate-400 hover:text-slate-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Radio className="h-5 w-5 text-slate-900" />
          <div>
            <h1 className="font-semibold text-slate-900">SMS Gateways</h1>
            <p className="text-xs text-slate-500">
              Each organisation sends on its own SIM and pays its own network bill
            </p>
          </div>
          <Button size="sm" className="ml-auto" onClick={() => setAdding((v) => !v)}
                  data-testid="add-gateway-btn">
            <Plus className="h-4 w-4 mr-1" /> {adding ? 'Close' : 'Register gateway'}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {adding && <SmsGatewayWizard onDone={() => {
          setAdding(false);
          qc.invalidateQueries({ queryKey: ['sms-gateways'] });
        }} />}

        <Tabs defaultValue="gateways">
          <TabsList>
            <TabsTrigger value="gateways" data-testid="tab-gateways">
              <Radio className="h-4 w-4 mr-1" /> Gateways
            </TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage">
              <BarChart3 className="h-4 w-4 mr-1" /> Usage
            </TabsTrigger>
            <TabsTrigger value="inbox" data-testid="tab-inbox">
              <Inbox className="h-4 w-4 mr-1" /> Replies
            </TabsTrigger>
            <TabsTrigger value="test" data-testid="tab-test">
              <Send className="h-4 w-4 mr-1" /> Test send
            </TabsTrigger>
          </TabsList>

          <TabsContent value="gateways" className="mt-4 space-y-4">
            {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
            {!isLoading && rows.length === 0 && (
              <Card data-testid="gateways-empty">
                <CardContent className="py-14 text-center">
                  <Radio className="h-7 w-7 mx-auto text-slate-300" />
                  <p className="mt-4 text-sm text-slate-500">
                    No gateway registered yet. Register one to send on your own SIM.
                  </p>
                </CardContent>
              </Card>
            )}
            {rows.map((row) => (
              <GatewayCard key={row.id} row={row} busy={lifecycle.isPending}
                           onLifecycle={(id, action) => lifecycle.mutate({ id, action })} />
            ))}
          </TabsContent>

          <TabsContent value="usage" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Usage by gateway</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-amber-700 mb-4">
                  Estimated carrier cost only — nothing posts to the ledger without an
                  approved charging policy.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="usage-table">
                    <thead className="text-left text-slate-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="py-2">Gateway</th><th>Organisation</th><th>Country</th>
                        <th>Bill payer</th><th>Month</th><th>Segments</th><th>Est. cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(usage?.items || []).map((u) => (
                        <tr key={u.gateway_id} className="border-t"
                            data-testid={`usage-row-${u.gateway_id}`}>
                          <td className="py-2">{u.name}</td>
                          <td>{u.organisation_name}</td>
                          <td>{u.country}</td>
                          <td>{u.billing_owner === 'organisation' ? u.organisation_name : 'FWM'}</td>
                          <td>{u.sent_this_month}</td>
                          <td>{u.segments_this_month}</td>
                          <td>{(u.estimated_cost_this_month || 0).toFixed(2)} {u.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inbox" className="mt-4 space-y-3">
            {(replies?.items || []).length === 0 && (
              <p className="text-sm text-slate-500" data-testid="inbox-empty">
                No replies yet. Incoming messages arrive through your gateway's signed webhook.
              </p>
            )}
            {(replies?.items || []).map((m) => (
              <Card key={m.id} data-testid={`inbound-${m.id}`}>
                <CardContent className="py-4">
                  <p className="text-sm font-medium">{m.from_number}</p>
                  <p className="text-sm text-slate-600">{m.body}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {m.received_at ? new Date(m.received_at).toLocaleString() : ''}
                  </p>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="test" className="mt-4">
            <Card><CardContent className="pt-6"><TestSend gateways={rows} /></CardContent></Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
